import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  CalendarClock,
  CalendarRange,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useFollowUpCalendar } from "@/api/followups/hooks";
import { CalendarSponsorCard } from "./CalendarSponsorCard";
import { LogFollowUpModal } from "./LogFollowUpModal";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ─────────────────────────────────────────────
// Section header (month view collapsible)
// ─────────────────────────────────────────────

function SectionHeader({ title, count, expanded, onToggle, dotColor }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between py-2 px-1 text-left group"
    >
      <div className="flex items-center gap-2">
        {dotColor && <span className={cn("h-2 w-2 rounded-full", dotColor)} />}
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-xs text-muted-foreground font-normal">
          ({count})
        </span>
      </div>
      {expanded ? (
        <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      ) : (
        <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      )}
    </button>
  );
}

// ─────────────────────────────────────────────
// Date header stamp (month view)
// ─────────────────────────────────────────────

function DateHeader({ dateKey }) {
  const date = new Date(dateKey + "T00:00:00");
  const todayKey = toDateKey(new Date());
  const isToday = dateKey === todayKey;

  return (
    <div className="flex items-center gap-3 py-3 mt-2">
      <div
        className={cn(
          "h-10 w-10 rounded-xl flex flex-col items-center justify-center shrink-0",
          isToday
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        <span className="text-[10px] leading-none font-medium uppercase">
          {date.toLocaleDateString("en-IN", { weekday: "short" })}
        </span>
        <span className="text-base font-bold leading-tight">
          {date.getDate()}
        </span>
      </div>
      <div>
        <p className="text-sm font-semibold">
          {date.toLocaleDateString("en-IN", { month: "short" })}
        </p>
        {isToday && (
          <p className="text-[10px] text-primary font-medium">Today</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────

function CalendarSkeleton() {
  return (
    <div className="pt-6 space-y-5">
      {[1, 2, 3].map((i) => (
        <div key={i}>
          <div className="flex items-center gap-3 mb-3">
            <Skeleton className="h-9 w-9 rounded-xl" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-36" />
              <Skeleton className="h-2.5 w-24" />
            </div>
          </div>
          <div className="space-y-2.5">
            <Skeleton className="h-32 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Compact card for week columns
// ─────────────────────────────────────────────

function WeekViewCard({ sponsor, isToday, onLogFollowUp, onPullToWorkspace }) {
  const [expanded, setExpanded] = useState(false);

  const statusBorder = {
    pending_first_call: "border-l-gray-300",
    awaiting_funds: "border-l-blue-400",
    active_installment: "border-l-purple-400",
    nurture: "border-l-amber-400",
    unreachable: "border-l-orange-400",
  };
  const borderColor =
    statusBorder[sponsor.followUpStatus] ?? "border-l-gray-300";

  return (
    <div
      className={cn(
        "rounded-xl border border-border/50 border-l-2 bg-background p-2.5 cursor-pointer",
        "hover:shadow-md transition-all duration-150",
        borderColor,
        expanded && "shadow-md",
      )}
      onClick={() => setExpanded((v) => !v)}
    >
      <p className="text-[11px] font-semibold leading-snug truncate">
        {sponsor.sponsorName}
      </p>
      <p className="text-[10px] text-muted-foreground truncate mt-0.5">
        {sponsor.mahalluName || sponsor.place || "—"}
      </p>
      <p className="text-[10px] font-bold text-amber-600 tabular-nums mt-1">
        ₹{sponsor.remaining.toLocaleString()} left
      </p>

      {expanded && (
        <div
          className="mt-2.5 pt-2.5 border-t border-border/40 space-y-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{
                width: `${Math.min(
                  sponsor.amountPromised > 0
                    ? (sponsor.paidAmount / sponsor.amountPromised) * 100
                    : 0,
                  100,
                )}%`,
              }}
            />
          </div>

          {sponsor.sponsorPhone && (
            <p className="text-[9px] text-muted-foreground">
              {sponsor.sponsorPhone}
            </p>
          )}

          <div className="flex flex-col gap-1 pt-1">
            <button
              onClick={() => onLogFollowUp(sponsor)}
              className="w-full text-[10px] font-medium py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              Log Follow-Up
            </button>
            <button
              onClick={() => onPullToWorkspace(sponsor)}
              className="w-full text-[10px] font-medium py-1 rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
            >
              Open Workspace →
            </button>
            {sponsor.sponsorPhone && (
              <button
                onClick={() =>
                  window.open(
                    `https://wa.me/${sponsor.sponsorPhone.replace(/\D/g, "")}`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="w-full text-[10px] font-medium py-1 rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors"
              >
                WhatsApp
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Day view
// ─────────────────────────────────────────────

function DayView({ data, currentDate, onLogFollowUp, onPullToWorkspace }) {
  const todayKey = toDateKey(new Date());
  const dateKey = toDateKey(currentDate);
  const isToday = dateKey === todayKey;

  const sponsors = data?.data?.scheduled?.[dateKey] ?? [];
  const overdue = data?.data?.overdue ?? [];
  const total = sponsors.length;

  return (
    <div className="flex flex-col">
      {/* Day summary strip */}
      <div
        className={cn(
          "mt-4 rounded-2xl p-4 flex items-center gap-4 border",
          isToday
            ? "bg-primary/5 border-primary/20"
            : "bg-muted/30 border-border/40",
        )}
      >
        <div
          className={cn(
            "flex flex-col items-center justify-center w-12 h-12 rounded-2xl shrink-0",
            isToday
              ? "bg-primary text-primary-foreground"
              : "bg-background border border-border/60",
          )}
        >
          <span className="text-[9px] font-bold uppercase tracking-wider leading-none">
            {currentDate.toLocaleDateString("en-IN", { month: "short" })}
          </span>
          <span className="text-xl font-bold leading-tight">
            {currentDate.getDate()}
          </span>
        </div>

        <div>
          <p className="text-sm font-semibold">
            {currentDate.toLocaleDateString("en-IN", { weekday: "long" })}
          </p>
          <p className="text-xs text-muted-foreground">
            {total === 0
              ? "No follow-ups scheduled"
              : `${total} follow-up${total !== 1 ? "s" : ""} scheduled`}
          </p>
        </div>

        {total > 0 && (
          <div className="ml-auto">
            <span
              className={cn(
                "text-xs font-semibold px-2.5 py-1 rounded-full",
                isToday
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-foreground",
              )}
            >
              {total} action{total !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Overdue banner (today only) */}
      {isToday && overdue.length > 0 && (
        <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-red-50 border border-red-100 px-4 py-2.5">
          <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <p className="text-xs font-medium text-red-700">
            {overdue.length} overdue action{overdue.length !== 1 ? "s" : ""}{" "}
            need attention
          </p>
        </div>
      )}

      {/* Cards */}
      <div className="pt-4 pb-8 space-y-3">
        {sponsors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <CalendarClock className="h-10 w-10 text-muted-foreground/20 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              No follow-ups for this day
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Use the arrows to navigate to a different day
            </p>
          </div>
        ) : (
          sponsors.map((sp) => (
            <CalendarSponsorCard
              key={sp._id}
              sponsor={sp}
              isToday={isToday}
              isOverdue={false}
              onLogFollowUp={onLogFollowUp}
              onPullToWorkspace={onPullToWorkspace}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Week view
// ─────────────────────────────────────────────

function WeekView({ data, currentDate, onLogFollowUp, onPullToWorkspace }) {
  const todayStr = toDateKey(new Date());

  const weekDays = useMemo(() => {
    const dow = currentDate.getDay();
    const diff = dow === 0 ? -6 : 1 - dow; // shift to Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(currentDate);
      d.setDate(currentDate.getDate() + diff + i);
      return d;
    });
  }, [currentDate]);

  const scheduled = data?.data?.scheduled ?? {};

  return (
    <div className="overflow-x-auto -mx-3 md:mx-0">
      <div className="flex min-w-[700px] divide-x divide-border/40 border rounded-2xl overflow-hidden">
        {weekDays.map((day) => {
          const dateKey = toDateKey(day);
          const daySponsors = scheduled[dateKey] ?? [];
          const isToday = dateKey === todayStr;

          return (
            <div
              key={dateKey}
              className={cn(
                "flex flex-col flex-1 min-w-[130px]",
                isToday && "bg-primary/[0.03]",
              )}
            >
              {/* Column header */}
              <div
                className={cn(
                  "flex flex-col items-center py-3 border-b bg-background/95 backdrop-blur sticky top-0 z-10",
                  isToday && "bg-primary/5",
                )}
              >
                <span
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-widest",
                    isToday ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  {day.toLocaleDateString("en-IN", { weekday: "short" })}
                </span>
                <div
                  className={cn(
                    "flex items-center justify-center w-7 h-7 rounded-full mt-0.5 text-sm font-bold",
                    isToday
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground",
                  )}
                >
                  {day.getDate()}
                </div>
                {daySponsors.length > 0 && (
                  <span
                    className={cn(
                      "text-[9px] font-semibold px-1.5 py-0.5 rounded-full mt-1",
                      isToday
                        ? "bg-primary/15 text-primary"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {daySponsors.length}
                  </span>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 p-2 space-y-2">
                {daySponsors.length === 0 ? (
                  <div className="flex items-center justify-center h-16">
                    <span className="text-[10px] text-muted-foreground/40">
                      —
                    </span>
                  </div>
                ) : (
                  daySponsors.map((sp) => (
                    <WeekViewCard
                      key={sp._id}
                      sponsor={sp}
                      isToday={isToday}
                      onLogFollowUp={onLogFollowUp}
                      onPullToWorkspace={onPullToWorkspace}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Month list view (existing behavior)
// ─────────────────────────────────────────────

function MonthListView({
  data,
  currentDate,
  onLogFollowUp,
  onPullToWorkspace,
}) {
  const [overdueExpanded, setOverdueExpanded] = useState(true);
  const [unscheduledExpanded, setUnscheduledExpanded] = useState(false);

  const calendarData = data?.data;
  const todayKey = toDateKey(new Date());

  const sortedScheduledKeys = useMemo(() => {
    if (!calendarData?.scheduled) return [];
    return Object.keys(calendarData.scheduled).sort();
  }, [calendarData?.scheduled]);

  const overdue = calendarData?.overdue ?? [];
  const unscheduled = calendarData?.unscheduled ?? [];
  const counts = calendarData?.counts;

  return (
    <div className="space-y-1">
      {/* Summary counts */}
      {counts && (
        <div className="flex items-center gap-4 pb-3 text-xs text-muted-foreground">
          <span>
            <span className="font-semibold text-foreground">
              {counts.scheduledTotal}
            </span>{" "}
            scheduled
          </span>
          <span>
            <span className="font-semibold text-red-500">
              {counts.overdueTotal}
            </span>{" "}
            overdue
          </span>
          <span>
            <span className="font-semibold text-foreground">
              {counts.unscheduledTotal}
            </span>{" "}
            unscheduled
          </span>
        </div>
      )}

      {/* Overdue */}
      {overdue.length > 0 && (
        <div className="rounded-2xl border border-red-100 bg-red-50/30 px-3 py-1">
          <SectionHeader
            title="Overdue"
            count={overdue.length}
            expanded={overdueExpanded}
            onToggle={() => setOverdueExpanded((v) => !v)}
            dotColor="bg-red-500"
          />
          {overdueExpanded && (
            <div className="space-y-2 pb-2">
              {overdue.map((sp) => (
                <CalendarSponsorCard
                  key={sp._id}
                  sponsor={sp}
                  isOverdue
                  onLogFollowUp={onLogFollowUp}
                  onPullToWorkspace={onPullToWorkspace}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {sortedScheduledKeys.length === 0 && overdue.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CalendarDays className="h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            No follow-ups scheduled for{" "}
            {currentDate.toLocaleDateString("en-IN", {
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
      )}

      {/* Scheduled by date */}
      {sortedScheduledKeys.map((dateKey) => {
        const sponsors = calendarData.scheduled[dateKey];
        const isToday = dateKey === todayKey;
        return (
          <div key={dateKey}>
            <DateHeader dateKey={dateKey} />
            <div className="space-y-2">
              {sponsors.map((sp) => (
                <CalendarSponsorCard
                  key={sp._id}
                  sponsor={sp}
                  isToday={isToday}
                  onLogFollowUp={onLogFollowUp}
                  onPullToWorkspace={onPullToWorkspace}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Unscheduled */}
      {unscheduled.length > 0 && (
        <div className="rounded-2xl border border-dashed px-3 py-1 mt-2">
          <SectionHeader
            title="Unscheduled"
            count={unscheduled.length}
            expanded={unscheduledExpanded}
            onToggle={() => setUnscheduledExpanded((v) => !v)}
            dotColor="bg-muted-foreground/50"
          />
          {unscheduledExpanded && (
            <div className="space-y-2 pb-2">
              {unscheduled.map((sp) => (
                <CalendarSponsorCard
                  key={sp._id}
                  sponsor={sp}
                  onLogFollowUp={onLogFollowUp}
                  onPullToWorkspace={onPullToWorkspace}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────

/**
 * FollowUpCalendarView
 * Props:
 *   onPullToWorkspace — (sponsor) => void
 */
export function FollowUpCalendarView({ onPullToWorkspace, scrollRef }) {
  const [calendarView, setCalendarView] = useState("month"); // "day" | "week" | "month"
  const [currentDate, setCurrentDate] = useState(new Date());
  const [typeFilter, setTypeFilter] = useState("all");
  const [logTarget, setLogTarget] = useState(null);

  // ── Date range per view ──
  const { from, to } = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const d = currentDate.getDate();

    if (calendarView === "day") {
      const start = new Date(y, m, d, 0, 0, 0, 0);
      const end = new Date(y, m, d, 23, 59, 59, 999);
      return { from: start.toISOString(), to: end.toISOString() };
    }

    if (calendarView === "week") {
      const dow = currentDate.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const mon = new Date(y, m, d + diff, 0, 0, 0, 0);
      const sun = new Date(y, m, d + diff + 6, 23, 59, 59, 999);
      return { from: mon.toISOString(), to: sun.toISOString() };
    }

    // month
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0, 23, 59, 59, 999);
    return { from: first.toISOString(), to: last.toISOString() };
  }, [currentDate, calendarView]);

  const { data, isLoading } = useFollowUpCalendar({
    from,
    to,
    type: typeFilter !== "all" ? typeFilter : undefined,
  });

  // ── Navigation ──
  const navigate = (direction) => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (calendarView === "day") d.setDate(d.getDate() + direction);
      if (calendarView === "week") d.setDate(d.getDate() + direction * 7);
      if (calendarView === "month") d.setMonth(d.getMonth() + direction);
      return d;
    });
  };

  const goToToday = () => setCurrentDate(new Date());

  // ── Header title ──
  const headerTitle = useMemo(() => {
    if (calendarView === "day") {
      return currentDate.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    if (calendarView === "week") {
      const dow = currentDate.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const mon = new Date(currentDate);
      mon.setDate(currentDate.getDate() + diff);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      const fmt = (d) =>
        d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
      return `${fmt(mon)} – ${fmt(sun)}`;
    }

    return currentDate.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  }, [currentDate, calendarView]);

  return (
    <div className="flex flex-col gap-0">
      {/* ── Controls bar ── */}
      <div className="flex items-center justify-between gap-3 py-3 flex-wrap">
        {/* Left: navigation */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(-1)}
            className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <span className="text-sm font-semibold min-w-[160px] text-center select-none">
            {headerTitle}
          </span>

          <button
            onClick={() => navigate(1)}
            className="h-7 w-7 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <button
            onClick={goToToday}
            className="text-xs text-primary font-medium px-2.5 py-1 rounded-full hover:bg-primary/10 transition-colors ml-1"
          >
            Today
          </button>
        </div>

        {/* Right: type filter + view toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Type filter */}
          <div className="flex items-center bg-muted rounded-full p-0.5 gap-0.5">
            {[
              { id: "all", label: "All" },
              { id: "Mahallu", label: "Mahallu" },
              { id: "Individual", label: "Individual" },
            ].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setTypeFilter(id)}
                className={cn(
                  "text-xs font-medium px-3 py-1 rounded-full transition-all duration-150",
                  typeFilter === id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex items-center bg-muted rounded-full p-0.5 gap-0.5">
            {[
              { id: "day", label: "Day", icon: CalendarClock },
              { id: "week", label: "Week", icon: CalendarRange },
              { id: "month", label: "Month", icon: CalendarDays },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setCalendarView(id)}
                className={cn(
                  "flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full transition-all duration-150",
                  calendarView === id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && <CalendarSkeleton />}

      {/* ── Day view ── */}
      {!isLoading && calendarView === "day" && (
        <DayView
          data={data}
          currentDate={currentDate}
          onLogFollowUp={setLogTarget}
          onPullToWorkspace={onPullToWorkspace}
        />
      )}

      {/* ── Week view ── */}
      {!isLoading && calendarView === "week" && (
        <WeekView
          data={data}
          currentDate={currentDate}
          onLogFollowUp={setLogTarget}
          onPullToWorkspace={onPullToWorkspace}
        />
      )}

      {/* ── Month view ── */}
      {!isLoading && calendarView === "month" && (
        <div ref={scrollRef} className="flex-1 overflow-y-auto">
          <MonthListView
            data={data}
            currentDate={currentDate}
            onLogFollowUp={setLogTarget}
            onPullToWorkspace={onPullToWorkspace}
          />
        </div>
      )}

      {/* ── Log Follow-Up Modal (shared across all views) ── */}
      {logTarget && (
        <LogFollowUpModal
          open={!!logTarget}
          onOpenChange={(open) => {
            if (!open) setLogTarget(null);
          }}
          sponsorshipId={logTarget._id}
          sponsor={logTarget}
        />
      )}
    </div>
  );
}
